import React from "react";
import { reactLocalStorage } from "reactjs-localstorage";
import Constants from "../../../util/Constants";
import DailySalesReportAPI from "../../../api/DailySalesReportAPI";
import ApiUiController from "../../../controller/ApiUiController";
import { CSVLink } from "react-csv";
import { Button, Table } from "react-bootstrap";
import { FaFilter } from "react-icons/fa";
import StackedChartComponent from "../chartComponent/MultiBarChart";
import FilterPannel from "../../filter/FilterPannel";
import moment from 'moment';
import CallAPI from "../../../api/CallAPI";

class CallStatusReport extends React.Component {
  constructor(props) {
    super(props);

    const model = reactLocalStorage.getObject(Constants.Model);
    const feedbackOptions = model.call.fields.feedback.options || [];
    const callStatusOptions = model.call.fields.call_status.options || [];
    const callDirectionOptions = model.call.fields.direction.options || [];
    const userOptions = model.user.options || [];

    // Table headers (display)
    this.headerArray = ['Dial', 'Total Talktime', 'Avg TalkTime'];
    callStatusOptions.forEach(status => this.headerArray.push(status.label));
    callDirectionOptions.forEach(status => this.headerArray.push(status.label));
    feedbackOptions.forEach(fd => this.headerArray.push(fd.label));

    // Map display headers -> SQL keys
    this.headerMap = {
      'Dial': 'Dial',
      'Total Talktime': 'Total_TalkTime',
      'Avg TalkTime': 'Avg_TalkTime'
    };
    callStatusOptions.forEach(status => {
      this.headerMap[status.label] = status.label.replace(/\s+/g, '_');
    });
    callDirectionOptions.forEach(status => {
      this.headerMap[status.label] = status.label.replace(/\s+/g, '_');
    });
    feedbackOptions.forEach(fd => {
      this.headerMap[fd.label] = fd.label.replace(/\s+/g, '_');
    });

    const fields = model.call.fields;
    this.fields = { date_time: fields['date_time'] };
    this.loggedInUserId = reactLocalStorage.getObject(Constants.LoggedInUser).user_id;

    this.state = {
      progressData: [],
      feedbackOptions,
      callDirectionOptions,
      userOptions,
      filters: [{
        field: "date_time",
        condition: "in",
        value: `${moment().startOf('month').format('YYYY-MM-DD HH:mm:ss')}~${moment().endOf('month').format('YYYY-MM-DD HH:mm:ss')}`
      }],
      tableData: [],
      series: [],
      inApiCall: false,
      isFilterOpen: true,
      exportData: [],
    };
  }

  componentDidMount() {
    this.fetchApi(this.state.filters, this.onSuccess, this.onFail);
  }

  fetchApi = (filters, onSuccess, onFail) => {
    this.setState({ inApiCall: true });
    CallAPI.getFixedCallStatusReportList(filters, onSuccess, onFail);
  };

  formatDuration(seconds) {
    if (!seconds || isNaN(seconds) || seconds <= 0) return '0s';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    let result = '';
    if (hrs > 0) result += `${hrs}hr `;
    if (mins > 0) result += `${mins}m `;
    if (secs > 0) result += `${secs}s`;
    return result.trim();
  }

  parseData = (response) => {
    const userMap = {};
    this.state.userOptions.forEach(user => {
      userMap[user.value] = user.name;
    });

    // Map response data by sales_person
    const responseMap = {};
    response.forEach(item => {
      responseMap[item.sales_person] = item;
    });

    // Build tableData
    const tableData = this.state.userOptions.map(user => {
      const item = responseMap[user.value] || {};
      const row = {};
      row['user'] = user.name;

      this.headerArray.forEach((headerKey) => {
        const sqlKey = this.headerMap[headerKey];
        if (['Dial'].includes(headerKey)) {
          row[headerKey] = item[sqlKey] || 0;
        } else if (headerKey === 'Total Talktime') {
          const seconds = Number(item[sqlKey]) || 0;
          row[`_${headerKey}_seconds`] = seconds;
          row[headerKey] = this.formatDuration(seconds);
        } else if (headerKey === 'Avg TalkTime') {
          const seconds = Number(item[sqlKey]) || 0;
          row[`_${headerKey}_seconds`] = seconds;
          row[headerKey] = this.formatDuration(seconds);
        } else {
          row[headerKey] = item[sqlKey] || 0;
        }
      });

      return row;
    });

    // Totals row
    const totalsRow = { user: 'Total' };
    const totalTalktimeSeconds = tableData.reduce((sum, row) => sum + (row['_Total Talktime_seconds'] || 0), 0);
    const avgTalktimeSeconds = tableData.length > 0
      ? Math.floor(tableData.reduce((sum, row) => sum + (row['_Avg TalkTime_seconds'] || 0), 0) / tableData.length)
      : 0;

    this.headerArray.forEach(headerKey => {
      if (headerKey === 'Total Talktime') {
        totalsRow[headerKey] = this.formatDuration(totalTalktimeSeconds);
      } else if (headerKey === 'Avg TalkTime') {
        totalsRow[headerKey] = this.formatDuration(avgTalktimeSeconds);
      } else {
        totalsRow[headerKey] = tableData.reduce((sum, row) => sum + (parseInt(row[headerKey]) || 0), 0);
      }
    });

    tableData.push(totalsRow);

    return { tableData };
  }

  onSuccess = (data) => {
    const { tableData } = this.parseData(data);
    const exportData = tableData;
    this.setState({ tableData, exportData, inApiCall: false });
  };

  onFail = (err) => {
    console.log("Failed to fetch data", err);
  };

  toggleFilter = () => {
    this.setState(prevState => ({ isFilterOpen: !prevState.isFilterOpen }));
  };

  onFilterChange = (updatedFilters) => {
    this.setState({ filters: updatedFilters },
      () => this.fetchApi(this.state.filters, this.onSuccess, this.onFail)
    );
  };

  render() {
    const { exportData, filters, isFilterOpen, inApiCall, userOptions, tableData } = this.state;
    let selectedUserId;
    filters.forEach(filter => filter.field === 'responsible_person' ? selectedUserId = filter.value.split(Constants.Opt).filter(Boolean) : null);
    const filteredUserOptions = userOptions.filter(user => {
      if (Boolean(selectedUserId)) {
        return selectedUserId?.some(id => parseInt(id) === user.value);
      }
      return true;
    });

    const table = (
      <Table bordered hover>
        <thead>
          <tr>
            <th></th>
            {this.headerArray.map((key, index) => <th key={index}>{key}</th>)}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={index}>
              <td>{row.user}</td>
              {this.headerArray.map((key, idx) => <td key={idx}>{row[key]}</td>)}
            </tr>
          ))}
        </tbody>
      </Table>
    );

    return (
      <ApiUiController inApiCall={inApiCall}>
        <div className="container-fluid p-3">
          <div className="row">
            <div className="col-md-8">
              <div className="row">
                <div className="col-md-6">
                  <h1>Call Status Report</h1>
                </div>
                <div className="col-md-6">
                  <CSVLink data={exportData} filename={`call_By_feedback_By_salesPerson_report.csv`}>
                    <Button className="buttons btn-success">Export</Button>
                  </CSVLink>
                </div>
              </div>
            </div>
            <div className="col-md-4 pt-1">
              <div className="row filterrow">
                <div className="col-md-12 lastcol">
                  <div className="m-right">
                    <div className="btn btn-lead btn-sm filter-button" onClick={this.toggleFilter}>
                      <FaFilter />
                      <span className="badge badge-danger badge-mini rounded-circle">
                        {this.state.filters.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row pt-3">
            <div className="container-fluid">
              <div className="row">
                <div className={`col-md-${isFilterOpen ? 9 : 12}`}>
                  <div className="col-md-12">{table}</div>
                </div>
                {isFilterOpen && (
                  <FilterPannel
                    filters={filters}
                    fields={this.fields}
                    onFilterChange={this.onFilterChange}
                    isFilterOpen={isFilterOpen}
                    restrict={false}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </ApiUiController>
    );
  }
}

export default CallStatusReport;




